import styled from 'styled-components';

import { IconBuilder } from '@components/icons/builder';
import { IconCheckmark } from '@components/icons/checkmark';
import { SizedRelativeSpinner } from '@components/loading-spinner';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { LoadingLabel } from 'api/ai-builder/requests';

type LoadingStateDisplayProps = {
  loadingLabel: LoadingLabel | null;
  isBuilding: boolean;
};

export const LoadingStateDisplay = ({ loadingLabel, isBuilding }: LoadingStateDisplayProps) => {
  if (!loadingLabel) {
    return null;
  }

  if (loadingLabel === 'Building workflow') {
    return <BuildingWorkflowState isBuilding={isBuilding} />;
  }

  return <DefaultLoadingState label={loadingLabel} />;
};

const BuildingWorkflowState = ({ isBuilding }: { isBuilding: boolean }) => {
  const { t } = useScopedTranslate(locale);

  return (
    <StatusRow>
      <ToolMessageContainer>
        <Stack orientation="horizontal" justify="space-between" align="center">
          <Stack orientation="horizontal" justify="center" align="center" space={2}>
            <IconBuilder color="elementPurple80" />
            <Text font="semibold12" text={t('buildingWorkflow')} />
          </Stack>
          {isBuilding ? (
            <SizedRelativeSpinner size={16} $skipMargin $borderWidth={1.5} />
          ) : (
            <IconCheckmark color="elementGreen100" />
          )}
        </Stack>
      </ToolMessageContainer>
    </StatusRow>
  );
};

const DefaultLoadingState = ({ label }: { label: string }) => {
  return (
    <StatusRow>
      <AssistantBorder>
        <AssistantMessageContainer>
          <Stack orientation="horizontal" justify="center" align="center" space={2}>
            <AIGradientSpinner size={24} $skipMargin $borderWidth={2} />
            <Text aiGradient font="semibold12" text={label} />
          </Stack>
        </AssistantMessageContainer>
      </AssistantBorder>
    </StatusRow>
  );
};

const StatusRow = styled.div({
  padding: `${pxToRem(8)} 0`,
});

const AIGradientSpinner = styled(SizedRelativeSpinner)<{ $borderWidth?: number }>(
  ({ $borderWidth = 2 }) => ({
    '&& > div::before': {
      border: 'none',
      padding: pxToRem($borderWidth),
      background: `conic-gradient(
        from -90deg,
        transparent 0deg 120deg,
        ${theme.color.elementTeal100} 210deg,
        ${theme.color.elementViolet100} 360deg
      )`,
      WebkitMask: `radial-gradient(
        farthest-side,
        transparent calc(100% - ${$borderWidth}px),
        #000 calc(100% - ${$borderWidth}px)
      )`,
      mask: `radial-gradient(
        farthest-side,
        transparent calc(100% - ${$borderWidth}px),
        #000 calc(100% - ${$borderWidth}px)
      )`,
    },
  })
);

const AssistantBorder = styled.div({
  width: '100%',
  borderRadius: pxToRem(8),
  padding: pxToRem(1),
  background: theme.gradient.aiSurface30,
});

const AssistantMessageContainer = styled.div({
  width: '100%',
  borderRadius: pxToRem(7),
  padding: pxToRem(12),
  background: theme.color.backgroundLightDark,
});

const ToolMessageContainer = styled.div({
  width: '100%',
  borderRadius: pxToRem(7),
  padding: pxToRem(12),
  background: 'transparent',
  border: `1px solid ${theme.color.border10}`,
});

const locale = {
  en: {
    buildingWorkflow: 'Working...',
  },
};
